import dayjs, { Dayjs } from 'dayjs';
import { ClientService } from '../service/client.service';
import { HuddleClientService } from '../service/huddle-client.service';

export interface CalendarEvent {
  id: string;
  type: PlatformKey;
  meetingName: string;
  start: Dayjs;
  end: Dayjs;
  date: Dayjs;
  description?: string;
  location?: string;
  meetingLink: string | null;
  selectedCalendar: string;
  organizer?: {
    organizerEmail: string;
    organizerName: string;
  };
  invitees: [
    {
      email: string;
      name: string;
    },
  ];
  status?: string;
}

export type UserType = 'currentUser' | 'client';
export type PlatformKey = 'Huddle' | 'Zoom' | 'Google Meet' | 'Microsoft Teams';

export const getPlatformKey = (event: any): PlatformKey => {
  if (event.type === 'huddle' || event.selectedCalendar === 'huddle') return 'Huddle';
  if (event.type === 'Zoom' || event.selectedCalendar?.startsWith('zoom')) return 'Zoom';
  if (event.type === 'Google' || event.selectedCalendar?.startsWith('google')) return 'Google Meet';
  if (event.type === 'MSTeams' || event.selectedCalendar?.startsWith('microsoft'))
    return 'Microsoft Teams';
  return 'Huddle';
};

const createBaseEvent = (raw: any, overrides: any): CalendarEvent => {
  return {
    id: raw.id || `event-${Date.now()}`,
    meetingName: overrides.meetingName || raw.meetingName,
    start: dayjs(raw.start),
    end: dayjs(raw.end),
    date: dayjs(raw.start).startOf('day'),
    type: getPlatformKey(raw),
    description: overrides.description || raw.description,
    meetingLink: overrides.meetingLink || raw.meetingLink,
    selectedCalendar: raw.selectedCalendar,
    invitees: raw.invitees || [],
    organizer: overrides.organizer || raw.organizer,
  };
};

export const transformHuddleEvent = (event: any) =>
  createBaseEvent(event, {
    meetingName: event.meetingName || event.meetingName || 'Huddle',
    meetingLink: event.meetingLink,
    type: 'huddle',
    description: event.description || 'Slack Huddle',
    selectedCalendar: 'huddle',
  });

export const transformZoomEvent = (event: any) =>
  createBaseEvent(event, {
    meetingName: event.meetingName,
    meetingLink: event.meetingLink,
    organizer: event.organizer?.id,
    description: event.description,
    selectedCalendar: event?.selectedCalendar,
  });

export const transformMicrosoftEvent = (event: any) => {
  const link = event.meetingLink || event.onlineMeeting?.joinUrl;
  return {
    ...createBaseEvent(event, {
      meetingName: event.subject,
      meetingLink: link,
      description: event.body,
      selectedCalendar: event?.selectedCalendar,
    }),
    id: event.id || `ms-${event.subject}-${event.start}`,
  };
};

export const transformGoogleEvent = (event: any) =>
  createBaseEvent(event, {
    meetingName: event.meetingName,
    meetingLink: event.meetingLink,
    organizer: event.organizer?.email,
    selectedCalendar: event?.selectedCalendar,
  });

export const fetchClientInfo = async (clientId: string, source: string) => {
  if (source === 'Huddle') {
    return await HuddleClientService.getClientHuddleDetails(clientId);
  } else {
    return await ClientService.getClientDetails(clientId);
  }
};

export const getVisibleEvents = (events: CalendarEvent[]) => {
  const MAX_VISIBLE = 2;
  if (events.length < 3) {
    return {
      visibleEvents: events,
      remainingCount: 0,
    };
  }
  return {
    visibleEvents: events.slice(0, MAX_VISIBLE),
    remainingCount: events.length - MAX_VISIBLE,
  };
};
